import React, { useCallback, useContext } from 'react'
import moment from 'moment'
import { SEVEN_DAYS_AFTER_PURCHASE, TWENTY_ONE_DAYS_BEFORE_CHECK_IN } from 'constants/cancellationPolicies'
import BodyText from 'LuxKit/Typography/BodyText'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import TextLink from 'LuxKit/TextLink'
import { INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import Clickable from 'components/Common/Clickable'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import styled from 'styled-components'
import BookingProtectionViaInfoModal from 'checkout/Components/CheckoutSteps/Review/BookingProtection/BookingProtectionViaInfoModal'
import ModalContext from 'contexts/ModalContext'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { BOOKING_PROTECTION_ELIGABILITY_DAY_RANGE } from 'constants/bookingProtection'
import OfferPropertyCancellationPolicyModal from './OfferPropertyCancellationPolicyModal'

const MiddledTooltipIcon = styled(LineInfoCircleIcon)`
  vertical-align: middle;
`

interface Props {
  propertyTimezoneOffset?: number;
  checkInDate?: string | moment.Moment;
  isFlashUSP?: boolean;
  isBookingProtectionEnabled?: boolean;
  cartIncludesFlights?: boolean;
}

function OfferPropertyCancellationPolicies(props: Props) {
  const { propertyTimezoneOffset, checkInDate, isFlashUSP, isBookingProtectionEnabled = false, cartIncludesFlights } = props
  const intlDateFormatter = useIntlDateFormatter()

  const startBasedOnToday = propertyTimezoneOffset ? moment().utcOffset(propertyTimezoneOffset).add(SEVEN_DAYS_AFTER_PURCHASE, 'days') : moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')
  const startBasedOnCheckIn = checkInDate && (propertyTimezoneOffset ? moment(checkInDate).utcOffset(propertyTimezoneOffset).subtract(TWENTY_ONE_DAYS_BEFORE_CHECK_IN, 'days') : moment(checkInDate).subtract(TWENTY_ONE_DAYS_BEFORE_CHECK_IN, 'days'))
  let startDate: moment.Moment | undefined
  if (startBasedOnCheckIn) {
    startDate = moment.min(startBasedOnToday, startBasedOnCheckIn)
  }

  const bookingProtectionLastEligibleRefundDate = checkInDate ? moment(checkInDate).add(BOOKING_PROTECTION_ELIGABILITY_DAY_RANGE, 'days') : undefined

  const showModal = useContext(ModalContext)

  const onItemClick = useCallback(() => {
    if (isBookingProtectionEnabled && startDate && !cartIncludesFlights) {
      showModal(<BookingProtectionViaInfoModal cancellationType="free" cancelUntilFullRefundDate={startDate} bookingProtectionLastEligibleRefundDate={bookingProtectionLastEligibleRefundDate} />)
    } else {
      showModal(<OfferPropertyCancellationPolicyModal startDate={startDate} checkInDate={checkInDate} />)
    }
  }, [isBookingProtectionEnabled, showModal, startDate, bookingProtectionLastEligibleRefundDate, cartIncludesFlights, checkInDate])

  if (startDate?.isBefore(moment())) {
    return null
  }

  return <>
    {!isFlashUSP && <Clickable onClick={onItemClick}>
      <BodyText variant="medium" weight="bold" as="span" colour="highlight-secondary">Free cancellation</BodyText>{' '}
      {!startDate && <BodyText variant="medium" as="span">within 7 days of purchase (Hotel only)</BodyText>}
      {startDate && <BodyText variant="medium" as="span">until {intlDateFormatter(startDate, INTL_DAY_MONTH_NAME_SHORT)} (Hotel only)</BodyText>}
      {' '}<MiddledTooltipIcon size="XS" />
    </Clickable>}
    {isFlashUSP && <TextLink
      size="medium"
      colour="highlight-secondary"
      underline={false}
      data-type="7day"
      onClick={onItemClick}
      startIcon={<LineRefundIcon />}
    >
      Free cancellation
    </TextLink>}
  </>
}

export default OfferPropertyCancellationPolicies
